import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowClockwise, EnvelopeSimple, CircleNotch } from "@phosphor-icons/react";

/**
 * v1.24.0 — Delivery log for a party. Shows the last N ledger email
 * attempts (cron + manual) with status + one-click retry on failures.
 */
export default function EmailDeliveryLog({ partyId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/parties/${partyId}/email-log`, { params: { limit: 15 } });
            setRows(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (partyId) load(); /* eslint-disable-next-line */ }, [partyId]);

    const retry = async (id) => {
        setRetrying(id);
        try {
            const { data } = await api.post(`/email-log/${id}/retry`);
            toast.success(`Resent to ${data.sent_to}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRetrying(null); }
    };

    if (!partyId) return null;

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-4" data-testid="party-email-log">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1.5">
                        <EnvelopeSimple size={12} weight="fill" /> Delivery Log
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">Last 15 statement email attempts for this party.</div>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="email-log-refresh"
                    className="rounded p-1.5 text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-50"
                    title="Refresh"
                >
                    <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            {loading ? (
                <div className="mt-3 text-xs text-slate-500">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="mt-3 rounded border border-dashed border-slate-200 bg-white/60 p-4 text-center text-xs text-slate-500" data-testid="email-log-empty">
                    No emails sent yet. Enable the schedule above or click "Send now" to test.
                </div>
            ) : (
                <div className="mt-3 overflow-hidden rounded-md border border-slate-200 bg-white">
                    <table className="min-w-full text-xs" data-testid="email-log-table">
                        <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-2 py-1.5 text-left">When</th>
                                <th className="px-2 py-1.5 text-left">Recipient</th>
                                <th className="px-2 py-1.5 text-left">Trigger</th>
                                <th className="px-2 py-1.5 text-left">Status</th>
                                <th className="px-2 py-1.5 text-right"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r) => {
                                const ts = (() => { if (!r.sent_at) return "—"; const d = new Date(r.sent_at); if (Number.isNaN(d.getTime())) return "—"; const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`; })();
                                return (
                                    <tr key={r.id} data-testid={`email-log-row-${r.id}`} className={r.status === "failed" ? "bg-rose-50/50" : ""}>
                                        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-slate-600">{ts}</td>
                                        <td className="px-2 py-1.5 text-slate-700">{r.recipient}</td>
                                        <td className="px-2 py-1.5">
                                            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${r.trigger === "manual" ? "bg-blue-100 text-blue-800" : r.trigger === "retry" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700"}`}>
                                                {r.trigger.replace("cron_", "")}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1.5">
                                            {r.status === "sent" ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                                    <CheckCircle size={12} weight="fill" /> Sent
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-rose-700 font-semibold" title={r.error || ""}>
                                                    <XCircle size={12} weight="fill" /> Failed
                                                </span>
                                            )}
                                            {r.status === "failed" && r.error && (
                                                <div className="mt-0.5 max-w-[220px] truncate text-[10px] text-rose-600" title={r.error}>{r.error}</div>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                            {r.status === "failed" && (
                                                <button
                                                    type="button"
                                                    onClick={() => retry(r.id)}
                                                    disabled={retrying === r.id}
                                                    data-testid={`email-log-retry-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded border border-blue-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-800 hover:bg-blue-50 disabled:opacity-50"
                                                >
                                                    {retrying === r.id ? <CircleNotch size={10} className="animate-spin" /> : <ArrowClockwise size={10} weight="bold" />}
                                                    Resend
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
